import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ models, isOpen, onClose, onRemoveModel, onAddToCart }) => {
  const [selectedSpecs, setSelectedSpecs] = useState(['price', 'components', 'polygons', 'rating']);

  const specs = [
    { key: 'price', label: 'Price', format: (value) => !value || value === 0 ? 'Free' : `$${value.toFixed(2)}` },
    { key: 'components', label: 'Components', format: (value) => value?.toLocaleString() || '0' },
    { key: 'polygons', label: 'Polygons', format: (value) => value?.toLocaleString() || 'N/A' },
    { key: 'rating', label: 'Rating', format: (value, model) => {
      const rating = Number(value) || 0;
      const count = model?.totalRatings || 0;
      return (
        <div className="flex items-center justify-center space-x-1">
          <Icon name="Star" size={14} className="text-warning fill-current" />
          <span>{rating.toFixed(1)} <span className="text-xs text-muted-foreground font-normal">({count})</span></span>
        </div>
      );
    }},
    { key: 'complexity', label: 'Complexity', format: (value) => value || 'N/A' }
  ];

  const toggleSpec = (specKey) => {
    setSelectedSpecs(prev => 
      prev?.includes(specKey) 
        ? prev?.filter(key => key !== specKey)
        : [...prev, specKey]
    );
  };

  const getBestValue = (specKey) => {
    if (models?.length === 0) return null;
    
    switch (specKey) {
      case 'price':
        return Math.min(...models?.map(m => m?.price || 0));
      case 'rating':
        return Math.max(...models?.map(m => Number(m?.averageRating) || 0));
      case 'components':
        return Math.max(...models?.map(m => m?.componentCount || 0));
      default:
        return null;
    }
  };

  const isHighlighted = (model, specKey) => {
    const bestValue = getBestValue(specKey);
    if (bestValue === null) return false;
    
    let modelValue;
    switch(specKey) {
        case 'price': modelValue = model?.price || 0; break;
        case 'rating': modelValue = model?.averageRating || 0; break;
        case 'components': modelValue = model?.componentCount || 0; break;
        default: return false;
    }
    
    // Don't highlight zeros unless it's price (free is good)
    if (modelValue === 0 && specKey !== 'price') return false;
    return modelValue === bestValue;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card rounded-lg shadow-floating max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Compare Models ({models?.length})
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Side-by-side comparison of selected 3D models
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto flex-1">
          {models?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="GitCompare" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Models to Compare</h3>
              <p className="text-muted-foreground">
                Select models from the catalog to start comparing their specifications.
              </p>
            </div>
          ) : (
            <>
              {/* Spec Selector */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-foreground mb-3">Compare Specifications</h3>
                <div className="flex flex-wrap gap-2">
                  {specs?.map((spec) => (
                    <button
                      key={spec?.key}
                      onClick={() => toggleSpec(spec?.key)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        selectedSpecs?.includes(spec?.key)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                      }`}
                    >
                      {spec?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comparison Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-4 border-b border-border font-medium text-foreground min-w-[150px]">
                        Specification
                      </th>
                      {models?.map((model) => (
                        <th key={model?.id} className="text-center p-4 border-b border-border min-w-[200px]">
                          <div className="flex flex-col items-center justify-between h-full space-y-3">
                            {/* Model Image */}
                            <div className="w-32 h-32 mx-auto bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                              <Image
                                src={model?.sellerImageUrls?.[0] || model?.thumbnailUrl || model?.thumbnailPath || "/assets/images/no_image.png"}
                                alt={model?.name || 'Thumbnail'}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Model Info */}
                            <div className="flex-1 w-full px-2">
                              <h4 className="font-medium text-foreground text-sm line-clamp-2 min-h-[2.5rem]">
                                {model?.name}
                              </h4>
                            </div>

                            {/* Actions */}
                            <div className="flex space-x-2 pt-2">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => onAddToCart(model?.id)}
                                iconName="ShoppingCart"
                                className="w-full"
                              >
                                Add to Cart
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => onRemoveModel(model?.id)}
                                title="Remove from comparison"
                              >
                                <Icon name="Trash2" size={16} className="text-error" />
                              </Button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {specs?.filter(spec => selectedSpecs?.includes(spec?.key))?.map((spec) => (
                        <tr key={spec?.key} className="border-b border-border">
                          <td className="p-4 font-medium text-foreground bg-muted/20">
                            {spec?.label}
                          </td>
                          {models?.map((model) => {
                            let value;
                            switch(spec.key) {
                                case 'price': value = model.price; break;
                                case 'rating': value = model.averageRating; break;
                                case 'components': value = model.componentCount; break;
                                default: value = model[spec.key]; break;
                            }
                              
                            return (
                              <td 
                                key={model?.id} 
                                className={`p-4 text-center ${
                                  isHighlighted(model, spec?.key) 
                                    ? 'bg-success/10 text-success font-semibold' :'text-foreground'
                                }`}
                              >
                                <div className="flex items-center justify-center">
                                  {isHighlighted(model, spec?.key) && (
                                    <Icon name="Crown" size={14} className="mr-1 text-success" />
                                  )}
                                  {spec?.format(value, model)}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h3 className="text-sm font-medium text-foreground mb-2">Comparison Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Most Components:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {models?.find(m => (m?.componentCount || 0) === getBestValue('components') && getBestValue('components') > 0)?.name || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Highest Rated:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {models?.find(m => (m?.averageRating || 0) === getBestValue('rating') && getBestValue('rating') > 0)?.name || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonTool;